// Shop-Artikelgruppen-Whitelist Sektion — wird in CustomersManagementTab eingebettet.
// Erlaubt Admin/Innendienst, global zu definieren, welche Easybill-Artikelgruppen
// im B2B-Shop für alle Shop-Kunden angeboten werden.
import { useEffect, useState, useCallback } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import { Tag, RefreshCw, Save, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

export default function ShopWhitelistSection() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [savedCount, setSavedCount] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch('/api/shop/article-groups');
    if (r.ok) {
      const d = await r.json();
      setGroups(d.items || []);
      setSavedCount(d.savedCount || 0);
    } else {
      toast.error('Easybill-Artikelgruppen konnten nicht geladen werden');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    setGroups(prev => prev.map(g => g.id === id ? { ...g, allowed: !g.allowed } : g));
  };

  const allowedCount = groups.filter(g => g.allowed).length;

  const save = async () => {
    setSaving(true);
    const ids = groups.filter(g => g.allowed).map(g => g.id);
    const r = await authFetch('/api/shop/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ allowedArticleGroupIds: ids }),
    });
    setSaving(false);
    if (r.ok) {
      toast.success(`Shop-Whitelist gespeichert (${ids.length} Gruppen freigegeben)`);
      setSavedCount(ids.length);
    } else {
      toast.error('Speichern fehlgeschlagen');
    }
  };

  const refreshCache = async () => {
    setRefreshing(true);
    const r = await authFetch('/api/shop/refresh-cache', { method: 'POST' });
    if (r.ok) {
      toast.success('Easybill-Cache geleert — Preise und Artikel werden beim nächsten Shop-Aufruf neu geladen.');
      await load();
    } else {
      toast.error('Cache-Refresh fehlgeschlagen');
    }
    setRefreshing(false);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4" data-testid="shop-whitelist-section">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-[#9ec043]" />
          <h3 className="text-sm font-semibold text-[#32373c]">Freigegebene Artikelgruppen (Shop-Whitelist)</h3>
          <span className="text-xs text-gray-400">
            {loading ? '…' : `${savedCount} von ${groups.length} freigegeben`}
          </span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 mb-3 mt-3 leading-relaxed">
            Wähle die <strong>Easybill-Artikelgruppen</strong>, die im B2B-Shop für alle freigegebenen Kunden
            sichtbar sind. Die kundenspezifischen Preise werden automatisch aus der jeweiligen Preisgruppe
            (SALEPRICE2..10) gezogen. Diese Whitelist ist <strong>unabhängig</strong> von der internen
            Whitelist unter „Easybill"-Einstellungen.
          </p>

          <div className="flex flex-wrap gap-2 mb-3">
            <button
              onClick={save}
              disabled={saving || loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#9ec043] text-white text-xs font-medium hover:bg-[#7ca735] disabled:opacity-50"
              data-testid="btn-save-shop-whitelist"
            >
              <Save className="w-3.5 h-3.5" /> {saving ? 'Speichere …' : `Speichern (${allowedCount})`}
            </button>
            <button
              onClick={refreshCache}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-xs hover:bg-gray-200 disabled:opacity-50"
              data-testid="btn-refresh-shop-cache"
              title="Erzwingt das Neu-Laden der Easybill-Artikel und Preisgruppen beim nächsten Aufruf"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Aktualisiere …' : 'Cache aktualisieren'}
            </button>
          </div>

          {loading ? (
            <div className="text-xs text-gray-400 py-2">Lade Artikelgruppen aus Easybill …</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 max-h-[400px] overflow-y-auto pr-1">
              {groups.map(g => (
                <label
                  key={g.id}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs transition-colors ${
                    g.allowed ? 'bg-[#9ec043]/10 text-[#7ca735]' : 'hover:bg-gray-50 text-gray-600'
                  }`}
                  data-testid={`shop-group-${g.id}`}
                >
                  <input
                    type="checkbox"
                    checked={g.allowed}
                    onChange={() => toggle(g.id)}
                    className="accent-[#9ec043]"
                  />
                  <span className="flex-1">{g.name}</span>
                  <span className="text-gray-300 tabular-nums">#{g.id}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
